import { useDeploymentMenuAccess } from '@/hooks/useDeploymentMenuAccess';
import { useEffect, useMemo, useState } from 'react';
import { X, ChevronDown, LogOut } from 'lucide-react';
import { useAuthStore } from '@/store/auth';
import { logout } from '@/lib/api';
import {
  NAV_SECTIONS,
  BUILDING_STAFF_NAV,
  type NavSection,
} from '@/config/nav';
import { filterNavSectionsForPersona } from '@/config/visibilityFilter';
import { resolvePersonaId } from '@evi/shared';
import { useTodayInbox } from '@/hooks/useTodayInbox';
import { todayBadge } from '@/lib/todayInbox';
import { activeMenuLocation, groupHoldsPage, matchesMenuHref, menuGroupKey, rememberedSection, writeOpenSection } from '@/lib/menuMemory';
import { useMenuGroups } from '@/hooks/useMenuGroups';
import { Modal } from '../Modal';
import formStyles from '../Form.module.css';
import styles from './MobileNavOverlay.module.css';

interface MobileNavOverlayProps {
  open: boolean;
  currentPath: string;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

/**
 * Full-screen mobile menu — slides in from the right at <1024px
 * when the hamburger fires. Inside: same NAV_SECTIONS the desktop
 * top nav uses, rendered as accordion sections. Headers stay
 * pinned; sections collapse/expand on tap.
 *
 * Visual: glassmorphic dark, full-width on phones, 360px on
 * tablet/larger. Slide animation = 240ms cubic-bezier(0.22, 1,
 * 0.36, 1) to feel like a native sheet.
 */
export function MobileNavOverlay({
  open, currentPath, onClose, onNavigate,
}: MobileNavOverlayProps) {
  const user = useAuthStore((s) => s.user);
  const assignedDeployment = useDeploymentMenuAccess();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  // Which groups (NavParent) are open: the same state and rules as the
  // desktop dropdown (hooks/useMenuGroups.ts; Warren, 28 October 2026).
  // A tap opens or closes one group and leaves the others, remembered for
  // this user; the group holding the page shown is open while you are on it.
  const groups = useMenuGroups(user?._id, currentPath);
  const search = typeof window === 'undefined' ? '' : window.location.search;

  // Persona-aware visibility filter: mirrors TopNav. Single shared
  // helper (filterNavSectionsForPersona) so the mobile drawer and
  // the desktop top-nav stay in lockstep automatically.
  const visibleSections = useMemo<NavSection[]>(() => {
    // Building staff: their one page (config/nav.ts BUILDING_STAFF_NAV).
    if (user?.facilities_only) return BUILDING_STAFF_NAV;
    const persona = resolvePersonaId({
      role: user?.role,
      // Same as TopNav: the two must resolve the same persona or the
      // mobile drawer and the desktop nav disagree about who you are.
      template: user?.template ?? null,
      is_sales_agent: user?.is_sales_agent ?? false,
    });
    return filterNavSectionsForPersona(NAV_SECTIONS, persona, user?.permissions ?? [], assignedDeployment);
  }, [user?.role, user?.template, user?.is_sales_agent, user?.permissions, user?.facilities_only, assignedDeployment]);

  // The Super Admin's menu (Warren, 21 October 2026, item M): Today first, navigating straight there with the total
  // waiting, and the section left open remembered per browser. Every other menu takes the branches it always took.
  const superMenu = visibleSections[0]?.id === 'today';
  const todayCount = todayBadge(useTodayInbox(superMenu).data?.total);

  // Pre-expand the section containing the active route when the drawer
  // opens, so the user sees the page they're on already surfaced; the
  // group holding it opens by itself (useMenuGroups). Calculated once
  // per open, in this person's own menu.
  useEffect(() => {
    if (!open || superMenu) return;
    const active = activeMenuLocation(visibleSections, currentPath, window.location.search);
    if (!active) return;
    setExpandedSection(active.sectionId);
  }, [open, currentPath, superMenu, visibleSections]);

  // The Super Admin's drawer opens on the section left open (remembered for this user), else on the one holding this
  // page, never on the EVI shortcuts' copy of it.
  useEffect(() => {
    if (!open || !superMenu) return;
    setExpandedSection(rememberedSection(visibleSections.map((s) => s.id), undefined, user?._id)
      ?? activeMenuLocation(visibleSections, currentPath, window.location.search)?.sectionId ?? null);
  }, [open, currentPath, superMenu, visibleSections, user?._id]);

  // ESC closes the overlay.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const handleNavigate = (href: string) => {
    onNavigate(href);
    onClose();
  };

  // In-app logout confirm — the overlay previously logged out on the
  // first tap with no confirmation; this adds the same styled
  // "Log out?" step the user chip menu gets, via the Modal primitive.
  const [confirmLogout, setConfirmLogout] = useState(false);
  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  return (
    <>
      {/* Backdrop — clicking dismisses the sheet. */}
      <div
        className={`${styles.backdrop} ${open ? styles.backdropOpen : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`${styles.sheet} ${open ? styles.sheetOpen : ''}`}
        aria-label="Main navigation"
        aria-hidden={!open}
      >
        <div className={styles.header}>
          <div className={styles.identity}>
            <span className={styles.avatar}>{user?.name?.[0]?.toUpperCase() ?? 'U'}</span>
            <div className={styles.identityLines}>
              <span className={styles.identityName}>{user?.name ?? 'Admin'}</span>
              {user?.email && <span className={styles.identityEmail}>{user.email}</span>}
            </div>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        <nav className={styles.body}>
          {visibleSections.map((section) => {
            const SectionIcon = section.icon;
            const isExpanded = expandedSection === section.id;
            const today = section.id === 'today' ? section.items[0] : undefined;
            if (today?.kind === 'leaf') {
              return (
                <div key={section.id} className={styles.section}>
                  <button
                    type="button"
                    className={`${styles.sectionHeader} ${matchesMenuHref(currentPath, today.href, search) ? styles.sectionHeaderOpen : ''}`}
                    onClick={() => handleNavigate(today.href)}
                    aria-label={todayCount ? `${section.label}, ${todayCount} waiting` : section.label}
                  >
                    <SectionIcon size={16} />
                    <span className={styles.sectionLabel}>{section.label}</span>
                    {todayCount && <span className={styles.sectionBadge} aria-hidden="true">{todayCount}</span>}
                  </button>
                </div>
              );
            }
            return (
              <div key={section.id} className={styles.section}>
                <button
                  type="button"
                  className={`${styles.sectionHeader} ${isExpanded ? styles.sectionHeaderOpen : ''}`}
                  onClick={() => {
                    // Groups keep their own open or closed state across
                    // sections (useMenuGroups), so nothing resets here.
                    setExpandedSection((p) => (p === section.id ? null : section.id));
                    if (superMenu) writeOpenSection(isExpanded ? null : section.id, undefined, user?._id);
                  }}
                  aria-expanded={isExpanded}
                >
                  <SectionIcon size={16} />
                  <span className={styles.sectionLabel}>{section.label}</span>
                  <ChevronDown
                    size={14}
                    className={`${styles.sectionChev} ${isExpanded ? styles.sectionChevOpen : ''}`}
                  />
                </button>
                {isExpanded && (
                  <div className={styles.sectionItems}>
                    {section.items.map((item) => {
                      if (item.kind === 'leaf') {
                        const Icon = item.icon;
                        const active = matchesMenuHref(currentPath, item.href, search);
                        return (
                          <button
                            key={item.id}
                            type="button"
                            className={`${styles.item} ${active ? styles.itemActive : ''}`}
                            onClick={() => handleNavigate(item.href)}
                          >
                            <Icon size={14} />
                            <span>{item.label}</span>
                          </button>
                        );
                      }
                      // NavParent, a collapsible group mirroring the
                      // desktop dropdown's. Header is a button toggling
                      // this group only (useMenuGroups); an empty group
                      // never renders. Children animate in and out via
                      // the max-height transition on .groupChildren /
                      // .groupChildrenOpen.
                      if (item.children.length === 0) return null;
                      const Icon = item.icon;
                      const groupKey = menuGroupKey(section.id, item.id);
                      const holdsPage = groupHoldsPage(item.children, currentPath, search);
                      const isParentOpen = groups.isOpen(groupKey, holdsPage);
                      return (
                        <div key={item.id} className={styles.group}>
                          <button
                            type="button"
                            className={`${styles.groupHeader} ${isParentOpen ? styles.groupHeaderOpen : ''}`}
                            onClick={() => groups.toggle(groupKey, holdsPage)}
                            aria-expanded={isParentOpen}
                          >
                            <Icon size={12} />
                            <span className={styles.groupLabel}>{item.label}</span>
                            <ChevronDown
                              size={12}
                              className={`${styles.groupChev} ${isParentOpen ? styles.groupChevOpen : ''}`}
                            />
                          </button>
                          <div
                            className={`${styles.groupChildren} ${isParentOpen ? styles.groupChildrenOpen : ''}`}
                            aria-hidden={!isParentOpen}
                          >
                            {item.children.map((child) => {
                              const ChildIcon = child.icon;
                              const active = matchesMenuHref(currentPath, child.href, search);
                              return (
                                <button
                                  key={child.id}
                                  type="button"
                                  className={`${styles.item} ${styles.itemChild} ${active ? styles.itemActive : ''}`}
                                  onClick={() => handleNavigate(child.href)}
                                  // Children of a collapsed parent
                                  // are visually hidden via max-height:0
                                  // but still in the DOM — tabIndex -1
                                  // keeps them out of the focus order
                                  // when the section's collapsed. Mirrors
                                  // TopNav.tsx:277.
                                  tabIndex={isParentOpen ? 0 : -1}
                                >
                                  <ChildIcon size={14} />
                                  <span>{child.label}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.logoutBtn}
            onClick={() => setConfirmLogout(true)}
          >
            <LogOut size={14} />
            <span>Log out</span>
          </button>
        </div>
      </aside>

      <Modal
        isOpen={confirmLogout}
        onClose={() => setConfirmLogout(false)}
        title="Log out?"
        size="sm"
        footer={
          <>
            <button
              type="button"
              className={formStyles.btnSecondary}
              onClick={() => setConfirmLogout(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={formStyles.btnPrimary}
              onClick={() => { void handleLogout(); }}
            >
              Log out
            </button>
          </>
        }
      >
        {/* Explicit body-text token: the modal mounts inside the
            overlay's dark chrome (hardcoded #f4f4f5 text), so without
            this the paragraph inherits near-white and vanishes on the
            light-mode modal surface. var(--color-tx) flips per theme. */}
        <p style={{ margin: 0, color: 'var(--color-tx)' }}>
          You&apos;ll be signed out of the EVI admin console.
        </p>
      </Modal>
    </>
  );
}
